import type { MistakeSeverity, PunishmentFacts, Theme } from '../../types';

/**
 * Génère les trois phrases pédagogiques d'un moment clé à partir de
 * données vérifiées (thèmes détectés, coup joué, meilleur coup, punition).
 * Purement structuré, jamais généré par un modèle de langage — le point
 * d'extension prévu pour brancher un LLM plus tard serait ici, en
 * remplaçant `explainMoment` par un appel qui reçoit les mêmes faits
 * vérifiés en entrée (aucun autre module n'aurait à changer).
 */
export interface ExplainInput {
  playedSan: string;
  bestSan: string;
  bestLineSan: string[];
  themes: Theme[];
  severity: MistakeSeverity;
  /** Faits vérifiés sur la réponse adverse qui punit le coup joué — quand
   * disponible, la première phrase devient concrète (pièce, case, défense)
   * au lieu d'une formule générique par thème. */
  punishment?: PunishmentFacts;
}

const PIECE_NAMES_FR: Record<string, string> = {
  p: 'ton pion',
  n: 'ton cavalier',
  b: 'ton fou',
  r: 'ta tour',
  q: 'ta dame',
};

/** Décrit concrètement ce que fait la réponse adverse (capture, mat, fourchette, échec),
 * par ordre de pertinence pédagogique. Retourne null si aucun fait spécifique n'est
 * exploitable (la réponse gagne du terrain sans motif mécaniquement identifiable). */
function punishmentDetail(punishment: PunishmentFacts): string | null {
  if (punishment.mateInMoves === 1) return 'fait échec et mat immédiatement';
  if (punishment.mateInMoves) return `lance un mat forcé en ${punishment.mateInMoves} coups`;
  if (punishment.captured) {
    const pieceName = PIECE_NAMES_FR[punishment.captured.piece] ?? 'une pièce';
    return punishment.captured.defenders > 0
      ? `prend ${pieceName} en ${punishment.captured.square} (que tu peux reprendre ensuite)`
      : `prend ${pieceName} en ${punishment.captured.square}, qui n'était défendue par aucune pièce`;
  }
  if (punishment.forkedSquares.length >= 2) {
    return `attaque en même temps deux de tes pièces (${punishment.forkedSquares.join(' et ')})`;
  }
  if (punishment.givesCheck) return 'met ton roi en échec';
  return null;
}

const THEME_OPPONENT_GAIN: Partial<Record<Theme, string>> = {
  pièce_en_prise: "de capturer une de tes pièces sans qu'elle soit défendue",
  fourchette: 'de jouer une fourchette qui attaque deux de tes pièces en même temps',
  mat_évitable: 'de forcer un mat dans les coups suivants',
  mat_manqué: 'de continuer la partie alors que tu avais un mat à disposition',
  sécurité_du_roi: 'de mettre ton roi en échec et de gagner du temps',
  clouage_exploité: "d'exploiter un clouage sur une de tes pièces",
  perte_matérielle: 'de prendre un avantage matériel',
  coup_plus_précis_disponible: 'de garder une position plus confortable',
};

const THEME_REFLEX: Partial<Record<Theme, string>> = {
  pièce_en_prise: 'Avant de jouer, vérifie systématiquement si chacune de tes pièces est attaquée et défendue.',
  fourchette: 'Repère les cases où un cavalier ou un pion adverse pourrait attaquer deux pièces à la fois.',
  mat_évitable: "Après chaque coup envisagé, vérifie les échecs et captures immédiats de l'adversaire.",
  mat_manqué: "Quand tu as une attaque qui avance, cherche s'il n'y a pas un mat plus direct avant de temporiser.",
  sécurité_du_roi: "Pense à la sécurité de ton roi avant de lancer une attaque ailleurs sur l'échiquier.",
  clouage_exploité: 'Repère les pièces clouées avant de les déplacer ou de compter sur leur défense.',
  perte_matérielle: 'Compte les échanges possibles sur la case avant de t’y engager.',
  coup_plus_précis_disponible: 'Prends un instant pour comparer deux ou trois candidats avant de jouer.',
};

const SEVERITY_LABEL: Record<MistakeSeverity, string> = {
  blunder: 'gaffe',
  mistake: 'erreur',
  inaccuracy: 'imprécision',
};

export function severityLabel(severity: MistakeSeverity): string {
  return SEVERITY_LABEL[severity];
}

/** Le réflexe associé à un thème, réutilisé hors d'un KeyMoment complet
 * (ex. carte « cause n°1 » du diagnostic, suggestion de puzzles par thème). */
export function reflexFor(theme: Theme): string {
  return THEME_REFLEX[theme] ?? 'Avant de jouer, compare toujours ton coup à une ou deux autres options.';
}

export function explainMoment(input: ExplainInput): { whatOpponentGets: string; betterOption: string; reflex: string } {
  const primaryTheme = input.themes[0];
  const opponentGain = (primaryTheme && THEME_OPPONENT_GAIN[primaryTheme]) ?? 'de garder une position plus favorable';
  const reflex =
    (primaryTheme && THEME_REFLEX[primaryTheme]) ??
    'Avant de jouer, compare toujours ton coup à une ou deux autres options.';

  const lineExcerpt = input.bestLineSan.slice(0, 3).join(' ');
  const betterOption = lineExcerpt
    ? `Le coup ${input.bestSan} était plus fort (variante indicative : ${lineExcerpt}...).`
    : `Le coup ${input.bestSan} était plus fort.`;

  const detail = input.punishment ? punishmentDetail(input.punishment) : null;
  const whatOpponentGets = detail
    ? `Après ${input.playedSan}, l'adversaire répond ${input.punishment!.replySan} et ${detail}.`
    : `En jouant ${input.playedSan}, tu permets à l'adversaire ${opponentGain}.`;

  return { whatOpponentGets, betterOption, reflex };
}
